import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, NGN } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { House, Users, Briefcase, Buildings, Plant, CalendarBlank, Handshake, ChatCircle, Newspaper, SignOut, Trash, MagnifyingGlass, CheckCircle, XCircle, PencilSimple, Wallet, IdentificationCard } from "@phosphor-icons/react";
import BlogEditorDialog from "@/components/BlogEditorDialog";

const SIDE = [{ to: "/admin", label: "Dashboard", icon: House }, { to: "/", label: "Public site", icon: House }];

const TAB_ITEMS = [
  { key: "stats", label: "Overview" },
  { key: "payments", label: "Payments" },
  { key: "kyc", label: "KYC" },
  { key: "investors", label: "Investors" },
  { key: "opportunities", label: "Opportunities" },
  { key: "projects", label: "Projects" },
  { key: "events", label: "Events" },
  { key: "blog", label: "Blog" },
  { key: "partnerships", label: "Partnerships" },
  { key: "contacts", label: "Contacts" },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [opps, setOpps] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [posts, setPosts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invs, setInvs] = useState([]);
  const [kycs, setKycs] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kycModal, setKycModal] = useState(null);

  const loadInvs = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (statusFilter) params.set("status", statusFilter);
    const r = await http.get(`/admin/investments?${params.toString()}`);
    setInvs(r.data);
  };

  const load = async () => {
    try {
      const [s, inv, o, pr, ev, bl, pa, co, kc] = await Promise.all([
        http.get("/admin/stats"), http.get("/admin/investors"),
        http.get("/opportunities"), http.get("/projects"),
        http.get("/events"), http.get("/blog"),
        http.get("/admin/partnerships"), http.get("/admin/contacts"),
        http.get("/admin/kyc"),
      ]);
      setStats(s.data); setInvestors(inv.data); setOpps(o.data); setProjects(pr.data);
      setEvents(ev.data); setPosts(bl.data); setPartners(pa.data); setContacts(co.data); setKycs(kc.data);
      await loadInvs();
    } catch { toast.error("Failed to load admin data"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => { loadInvs(); /* eslint-disable-next-line */ }, [q, statusFilter]);

  const del = async (kind, id) => {
    if (!confirm("Delete this item?")) return;
    try { await http.delete(`/admin/${kind}/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); }
  };

  const confirmPayment = async (invId) => {
    try { await http.post(`/admin/investments/${invId}/confirm`); toast.success("Payment confirmed"); loadInvs(); load(); }
    catch { toast.error("Failed"); }
  };

  const reviewKyc = async (userId, status, notes = "") => {
    try {
      await http.post(`/admin/kyc/${userId}/review`, { status, notes });
      toast.success(`KYC ${status}`);
      setKycModal(null);
      load();
    } catch { toast.error("Failed"); }
  };

  const statCards = stats ? [
    { label: "Investors", value: stats.investors, icon: Users },
    { label: "Investments", value: stats.investments, icon: Wallet },
    { label: "Pending Payments", value: stats.investments_pending, icon: CalendarBlank },
    { label: "Confirmed", value: stats.investments_successful, icon: CheckCircle },
    { label: "KYC Pending", value: stats.kyc_pending, icon: IdentificationCard },
    { label: "Opportunities", value: stats.opportunities, icon: Briefcase },
    { label: "Blog Posts", value: stats.blog_posts, icon: Newspaper },
    { label: "Newsletter", value: stats.newsletter_subscribers, icon: Plant },
  ] : [];

  const STATUS_COLORS = {
    successful: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-800",
    under_review: "bg-orange-100 text-orange-800",
    failed: "bg-red-100 text-red-800",
    refunded: "bg-slate-200 text-slate-700",
    completed: "bg-blue-100 text-blue-800",
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 mayo-navy-bg text-white flex-col p-6 z-40">
        <Link to="/" className="flex items-center gap-2 mb-10">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
          <span className="font-display font-black text-xl">MAYOTO</span>
        </Link>
        <p className="text-xs text-[#d4af37] font-display font-bold tracking-widest mb-4">ADMIN CONSOLE</p>
        <nav className="flex-1 space-y-1">
          {SIDE.map(n => (
            <Link key={n.label} to={n.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5"><n.icon size={18} />{n.label}</Link>
          ))}
        </nav>
        <button onClick={async () => { await logout(); nav("/"); }} className="text-left text-sm text-slate-300 hover:text-white flex items-center gap-2 mt-6" data-testid="admin-logout"><SignOut size={16} /> Log out</button>
      </aside>

      <main className="flex-1 lg:pl-64">
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
          <div className="px-6 lg:px-10 h-16 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Admin Console</p>
              <p className="font-display font-bold text-[#0a1128]" data-testid="admin-hello">{user?.full_name}</p>
            </div>
            <div className="flex items-center gap-2">
              {stats?.mock_payments && <Badge className="bg-slate-800 text-white hover:bg-slate-800">PAYSTACK SANDBOX</Badge>}
              <Badge className="bg-[#00b4d8] text-white hover:bg-[#00b4d8]">ADMIN</Badge>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-10">
          <Tabs defaultValue="stats">
            <TabsList className="bg-white border border-slate-200 p-1 rounded-full flex flex-wrap h-auto" data-testid="admin-tabs">
              {TAB_ITEMS.map(t => (
                <TabsTrigger key={t.key} value={t.key} className="rounded-full data-[state=active]:bg-[#0a1128] data-[state=active]:text-white px-4 py-2 text-xs" data-testid={`admin-tab-${t.key}`}>{t.label}</TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="stats" className="mt-8">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map(s => (
                  <div key={s.label} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid={`admin-stat-${s.label.replace(/\s+/g,"-").toLowerCase()}`}>
                    <div className="flex items-center justify-between"><p className="text-sm text-slate-500">{s.label}</p><s.icon size={18} className="text-[#00b4d8]" /></div>
                    <p className="stat-number text-3xl text-[#0a1128] mt-3">{s.value}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="payments" className="mt-8">
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
                  <div className="relative flex-1">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input placeholder="Search investor, reference, account, opportunity…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="admin-payments-search" />
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {["", "pending", "successful", "under_review", "failed", "refunded"].map(s => (
                      <button key={s} onClick={() => setStatusFilter(s)} className={`text-xs px-3 py-1.5 rounded-full ${statusFilter === s ? "bg-[#0a1128] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`} data-testid={`admin-status-${s || "all"}`}>
                        {s ? s.replace("_"," ").toUpperCase() : "ALL"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>{["Investor","Investment","Amount","Reference","Account","Date","Status",""].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {invs.map((i, idx) => (
                        <tr key={i.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-pay-row-${idx}`}>
                          <td className="px-4 py-3"><p className="font-semibold text-[#0a1128]">{i.user_name}</p><p className="text-xs text-slate-500">{i.user_email}</p></td>
                          <td className="px-4 py-3 text-slate-700">{i.opportunity_name}</td>
                          <td className="px-4 py-3 font-semibold">{NGN(i.amount)}</td>
                          <td className="px-4 py-3 font-mono text-xs">{i.reference}</td>
                          <td className="px-4 py-3 text-xs">{i.virtual_account_number}<br/><span className="text-slate-400">{i.virtual_bank_name}</span></td>
                          <td className="px-4 py-3 text-xs">{new Date(i.created_at).toLocaleString()}</td>
                          <td className="px-4 py-3"><Badge className={`${STATUS_COLORS[i.status] || "bg-slate-100"} hover:${STATUS_COLORS[i.status]}`}>{i.status.replace("_"," ").toUpperCase()}</Badge></td>
                          <td className="px-4 py-3 text-right">
                            {i.status === "pending" && (
                              <Button size="sm" onClick={() => confirmPayment(i.id)} className="bg-emerald-600 hover:bg-emerald-700 rounded-full text-xs" data-testid={`admin-confirm-${idx}`}>Mark as Received</Button>
                            )}
                          </td>
                        </tr>
                      ))}
                      {invs.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-500">No payments yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="kyc" className="mt-8">
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>{["Investor","Email","ID Type","ID Number","Status","Submitted",""].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {kycs.map((k, idx) => (
                        <tr key={k.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-kyc-row-${idx}`}>
                          <td className="px-4 py-3 font-semibold text-[#0a1128]">{k.user_name}</td>
                          <td className="px-4 py-3">{k.user_email}</td>
                          <td className="px-4 py-3">{k.id_type}</td>
                          <td className="px-4 py-3 font-mono text-xs">{k.id_number}</td>
                          <td className="px-4 py-3"><Badge className={k.status === "verified" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : k.status === "rejected" ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>{k.status.toUpperCase()}</Badge></td>
                          <td className="px-4 py-3 text-xs">{new Date(k.submitted_at).toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="outline" onClick={() => setKycModal(k)} data-testid={`admin-kyc-view-${idx}`}>Review</Button>
                          </td>
                        </tr>
                      ))}
                      {kycs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No submissions yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              <Dialog open={!!kycModal} onOpenChange={(o) => !o && setKycModal(null)}>
                <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto">
                  {kycModal && <>
                    <DialogHeader><DialogTitle className="font-display">Review KYC — {kycModal.user_name}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div><p className="text-slate-500 text-xs">ID Type</p><p className="font-semibold">{kycModal.id_type}</p></div>
                        <div><p className="text-slate-500 text-xs">ID Number</p><p className="font-mono">{kycModal.id_number}</p></div>
                      </div>
                      <div>
                        <p className="text-slate-500 text-xs mb-1">ID Document</p>
                        {kycModal.id_document_b64?.startsWith("data:image") ? <img src={kycModal.id_document_b64} alt="ID" className="max-h-96 rounded-xl border" /> :
                         kycModal.id_document_b64 ? <a href={kycModal.id_document_b64} target="_blank" rel="noreferrer" className="text-[#00b4d8] underline">Open document</a> : <p className="text-slate-500">No document</p>}
                      </div>
                      {kycModal.selfie_b64 && <div><p className="text-slate-500 text-xs mb-1">Selfie</p><img src={kycModal.selfie_b64} alt="Selfie" className="max-h-64 rounded-xl border" /></div>}
                      <div>
                        <Textarea id="kyc-notes" placeholder="Optional reviewer notes (shown to investor on rejection)" data-testid="admin-kyc-notes" />
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button onClick={() => reviewKyc(kycModal.user_id, "rejected", document.getElementById("kyc-notes")?.value || "")} className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="admin-kyc-reject"><XCircle size={16} className="mr-1" />Reject</Button>
                        <Button onClick={() => reviewKyc(kycModal.user_id, "verified")} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid="admin-kyc-approve"><CheckCircle size={16} className="mr-1" />Approve</Button>
                      </div>
                    </div>
                  </>}
                </DialogContent>
              </Dialog>
            </TabsContent>

            <TabsContent value="investors" className="mt-8">
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200"><tr>{["Name","Email","Verified","KYC","Referrals","Joined",""].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{h}</th>)}</tr></thead>
                    <tbody>
                      {investors.map((u, i) => (
                        <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-investor-row-${i}`}>
                          <td className="px-4 py-3 font-semibold text-[#0a1128]">{u.full_name || "—"}</td>
                          <td className="px-4 py-3">{u.email}</td>
                          <td className="px-4 py-3">
                            {u.email_verified
                              ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid={`admin-inv-verified-${i}`}>VERIFIED</Badge>
                              : <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid={`admin-inv-unverified-${i}`}>UNVERIFIED</Badge>}
                          </td>
                          <td className="px-4 py-3">
                            <Badge className={u.kyc_status === "verified" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : u.kyc_status === "rejected" ? "bg-red-100 text-red-800 hover:bg-red-100" : "bg-slate-100 text-slate-700 hover:bg-slate-100"}>
                              {(u.kyc_status || "pending").toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">{u.referral_count || 0}</td>
                          <td className="px-4 py-3 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                          <td className="px-4 py-3 text-right flex justify-end gap-2 whitespace-nowrap">
                            {!u.email_verified && (
                              <>
                                <Button size="sm" variant="outline" onClick={async () => {
                                  try {
                                    const r = await http.post(`/admin/users/${u.id}/resend-verification`);
                                    if (r.data.sent) toast.success("Verification email sent.");
                                    else if (r.data.already_verified) toast.info("Already verified.");
                                    else toast.error(`Email delivery blocked: ${r.data.delivery_error || "unknown"}. Copy the link and share manually.`);
                                    if (r.data.verify_url) {
                                      try { await navigator.clipboard.writeText(r.data.verify_url); toast.info("Verify link copied to clipboard."); } catch {}
                                    }
                                    load();
                                  } catch { toast.error("Failed to resend verification"); }
                                }} data-testid={`admin-inv-resend-${i}`}>Resend link</Button>
                                <Button size="sm" onClick={async () => {
                                  if (!confirm(`Mark ${u.email} as verified without email confirmation?`)) return;
                                  try {
                                    await http.post(`/admin/users/${u.id}/verify-email`);
                                    toast.success("User marked as verified.");
                                    load();
                                  } catch { toast.error("Verify failed"); }
                                }} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full" data-testid={`admin-inv-verify-${i}`}>
                                  <CheckCircle size={14} className="mr-1" /> Verify
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                      {investors.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No investors yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {[
              { key: "opportunities", data: opps, cols: ["name","category","minimum_investment","duration","status"], deletable: true, kind: "opportunities" },
              { key: "projects", data: projects, cols: ["name","sector","location","status"], deletable: true, kind: "projects" },
              { key: "events", data: events, cols: ["title","date","venue","ticket_type"], deletable: true, kind: "events" },
              { key: "partnerships", data: partners, cols: ["company_name","contact_person","email","industry","partnership_type","created_at"], deletable: false },
              { key: "contacts", data: contacts, cols: ["name","email","subject","created_at"], deletable: false },
            ].map(t => (
              <TabsContent key={t.key} value={t.key} className="mt-8">
                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b border-slate-200"><tr>{t.cols.map(c => <th key={c} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{c.replace(/_/g," ")}</th>)}{t.deletable && <th className="px-4 py-3"></th>}</tr></thead>
                      <tbody>
                        {t.data.map((row, i) => (
                          <tr key={row.id || i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-row-${t.key}-${i}`}>
                            {t.cols.map(c => (
                              <td key={c} className="px-4 py-3 text-slate-700">
                                {c.includes("date") || c.includes("_at") ? (row[c] ? new Date(row[c]).toLocaleDateString() : "—") :
                                 c === "minimum_investment" ? NGN(row[c]) : (row[c] || "—")}
                              </td>
                            ))}
                            {t.deletable && <td className="px-4 py-3 text-right"><button onClick={() => del(t.kind, row.id)} className="text-red-500 hover:text-red-700" data-testid={`admin-del-${t.key}-${i}`}><Trash size={16} /></button></td>}
                          </tr>
                        ))}
                        {t.data.length === 0 && <tr><td colSpan={t.cols.length + 1} className="px-4 py-10 text-center text-slate-500">No records yet.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>
            ))}

            <TabsContent value="blog" className="mt-8">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-500">{posts.length} posts published</p>
                <BlogEditorDialog onSaved={load} />
              </div>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200"><tr>{["Title","Category","Author","Published",""].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold tracking-wider text-slate-500 uppercase">{h}</th>)}</tr></thead>
                    <tbody>
                      {posts.map((p, i) => (
                        <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-blog-row-${i}`}>
                          <td className="px-4 py-3 font-semibold text-[#0a1128]">{p.title}</td>
                          <td className="px-4 py-3">{p.category}</td>
                          <td className="px-4 py-3">{p.author}</td>
                          <td className="px-4 py-3 text-xs">{new Date(p.published_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3 text-right flex justify-end gap-1">
                            <BlogEditorDialog post={p} onSaved={load} trigger={<Button size="sm" variant="outline" data-testid={`admin-blog-edit-${i}`}><PencilSimple size={14} /></Button>} />
                            <button onClick={() => del("blog", p.id)} className="text-red-500 hover:text-red-700 p-2" data-testid={`admin-del-blog-${i}`}><Trash size={16} /></button>
                          </td>
                        </tr>
                      ))}
                      {posts.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">No posts yet.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}
